import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FaPlus, FaSave, FaTrash, FaSync, FaArrowUp, FaArrowDown, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import api from '../../../api/apiClient';

const initialFeatureState = {
  id: null,
  title: '',
  subtitle: '',
  icon: '',
  description_html: '',
  is_active: true,
  is_highlighted: false
};

const FeatureDescriptionEditor = ({ value, onChange, disabled }) => {
  const isUpdatingFromProps = useRef(false);
  
  const editor = useEditor(
    {
      extensions: [StarterKit],
      content: value || '',
      editable: !disabled,
      onUpdate({ editor }) {
        if (isUpdatingFromProps.current) return;
        const html = editor.getHTML();
        onChange(html);
      }
    },
    []
  );

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      isUpdatingFromProps.current = true;
      editor.commands.setContent(value || '', false);
      setTimeout(() => {
        isUpdatingFromProps.current = false;
      }, 0);
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className="border rounded p-2 bg-gray-50 text-gray-500 text-sm">Loading editor...</div>
    );
  }

  return (
    <div className="border rounded" style={{ minHeight: '160px' }}>
      <EditorContent editor={editor} className="p-2" />
    </div>
  );
};

const FeatureManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [features, setFeatures] = useState([]);
  const [featureForm, setFeatureForm] = useState(initialFeatureState);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);

  const [savingFeature, setSavingFeature] = useState(false);

  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterHighlighted, setFilterHighlighted] = useState('all');

  const normalize = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const loadFeatures = useCallback(async () => {
    const params = {};
    if (search && search.trim()) params.search = search.trim();
    if (filterActive === 'active') params.is_active = '1';
    if (filterActive === 'inactive') params.is_active = '0';
    if (filterHighlighted === 'highlighted') params.is_highlighted = '1';
    if (filterHighlighted === 'non-highlighted') params.is_highlighted = '0';

    const response = await api.features.list(params);
    if (response.data?.success) {
      setFeatures(response.data.data || []);
    }
  }, [search, filterActive, filterHighlighted]);

  const fetchAll = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) setLoading(true);
      await loadFeatures();
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [loadFeatures]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchAll({ showLoader: false });
    } finally {
      setRefreshing(false);
    }
  }, [fetchAll, loading, refreshing]);

  const sortedFeatures = useMemo(() => {
    return [...features].sort((a, b) => a.position - b.position || new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  }, [features]);

  const handleFeatureFormChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setFeatureForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleFeatureDescriptionChange = useCallback((html) => {
    setFeatureForm((prev) => ({
      ...prev,
      description_html: html || ''
    }));
  }, []);

  const handleNewFeature = useCallback(() => {
    setSelectedFeatureId(null);
    setFeatureForm(initialFeatureState);
  }, []);

  const handleEditFeature = useCallback((feature) => {
    if (!feature) return;
    setSelectedFeatureId(feature.id);
    setFeatureForm({
      id: feature.id,
      title: feature.title || '',
      subtitle: feature.subtitle || '',
      icon: feature.icon || '',
      description_html: feature.description_html || '',
      is_active: !!feature.is_active,
      is_highlighted: !!feature.is_highlighted
    });
  }, []);

  const handleSaveFeature = useCallback(async (event) => {
    event.preventDefault();

    const title = normalize(featureForm.title);
    const description_html = featureForm.description_html || '';
    if (!title || !description_html.trim()) return;

    setSavingFeature(true);
    try {
      const payload = {
        title,
        subtitle: normalize(featureForm.subtitle) || null,
        icon: normalize(featureForm.icon) || null,
        description_html,
        is_active: !!featureForm.is_active,
        is_highlighted: !!featureForm.is_highlighted
      };

      if (featureForm.id) {
        await api.features.update(featureForm.id, payload);
      } else {
        await api.features.create(payload);
      }

      await loadFeatures();
      handleNewFeature();
    } finally {
      setSavingFeature(false);
    }
  }, [featureForm, handleNewFeature, loadFeatures, normalize]);

  const handleDeleteFeature = useCallback(async (featureId) => {
    if (!featureId) return;
    await api.features.remove(featureId);
    if (selectedFeatureId === featureId) {
      handleNewFeature();
    }
    await loadFeatures();
  }, [handleNewFeature, loadFeatures, selectedFeatureId]);

  const changeFeatureOrder = useCallback(async (featureId, direction) => {
    const items = sortedFeatures.map((f) => ({ id: f.id, position: f.position }));
    const index = items.findIndex((x) => x.id === featureId);
    if (index === -1) return;

    if (direction === 'up' && index > 0) {
      const tmp = items[index - 1].position;
      items[index - 1].position = items[index].position;
      items[index].position = tmp;
    }
    if (direction === 'down' && index < items.length - 1) {
      const tmp = items[index + 1].position;
      items[index + 1].position = items[index].position;
      items[index].position = tmp;
    }

    await api.features.reorder(items);
    await loadFeatures();
  }, [loadFeatures, sortedFeatures]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  return (
    <div className="p-3 sm:p-6 md:p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/site-management')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h3 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Feature Management</h3>
            <p className="text-sm text-gray-600 mt-1">Manage product features with titles, icons and rich descriptions.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? 'Refreshing...' : (<><FaSync className="inline-block mr-2"/> Refresh</>)}
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleNewFeature}
            disabled={savingFeature}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="inline-block mr-2" /> New Feature
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-5">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Search</label>
            <div className="flex items-center bg-white border border-purple-200 rounded-lg px-3 py-2">
              <FaSearch className="text-gray-400 mr-2" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, subtitle or description..." className="w-full outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
            <select value={filterActive} onChange={(e) => setFilterActive(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Highlight</label>
            <select value={filterHighlighted} onChange={(e) => setFilterHighlighted(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
              <option value="all">All</option>
              <option value="highlighted">Highlighted only</option>
              <option value="non-highlighted">Non-highlighted only</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h6 className="text-lg font-bold mb-3">Features</h6>
          {loading ? (
            <div className="text-center py-6"><div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div></div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
              <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="text-left py-3 px-4">Title</th>
                    <th className="text-center py-3 px-4" style={{ width: '110px' }}>Highlight</th>
                    <th className="text-center py-3 px-4" style={{ width: '100px' }}>Status</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-purple-50">
                  {sortedFeatures.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-gray-500 py-6">No features found. Use the editor below to create your first feature.</td>
                    </tr>
                  ) : (
                    sortedFeatures.map((feature, index) => (
                      <tr key={feature.id}>
                        <td className="py-3 px-4 align-top">
                          <div>
                            <button onClick={() => handleEditFeature(feature)} className="text-left text-sm font-medium text-purple-800 hover:underline">{feature.title}</button>
                            {feature.subtitle && <div className="text-gray-500 text-xs">{feature.subtitle}</div>}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-left">
                          {feature.is_highlighted ? (
                            <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">Highlighted</span>
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Normal</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-left">
                          {feature.is_active ? (
                            <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold">Active</span>
                          ) : (
                            <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Inactive</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="d-flex gap-1 justify-content-end">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeFeatureOrder(feature.id, 'up')}
                              disabled={index === 0}
                              title="Move Up"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowUp className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeFeatureOrder(feature.id, 'down')}
                              disabled={index === sortedFeatures.length - 1}
                              title="Move Down"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowDown className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleDeleteFeature(feature.id)}
                              title="Delete Feature"
                              className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                            >
                              <FaTrash className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <h6 className="text-lg font-bold mb-3">{selectedFeatureId ? 'Edit Feature' : 'New Feature'}</h6>
          <form onSubmit={handleSaveFeature} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" name="title" value={featureForm.title} onChange={handleFeatureFormChange} disabled={savingFeature} placeholder="Feature title" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Subtitle (optional)</label>
                <input type="text" name="subtitle" value={featureForm.subtitle} onChange={handleFeatureFormChange} disabled={savingFeature} placeholder="Short supporting text" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Icon (optional)</label>
                <input type="text" name="icon" value={featureForm.icon} onChange={handleFeatureFormChange} disabled={savingFeature} placeholder="e.g. fa-star, heroicon name or custom key" className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
                <p className="text-xs text-gray-500 mt-1">This is a key used by your frontend to render an icon (no upload).</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_active" checked={featureForm.is_active} onChange={handleFeatureFormChange} disabled={savingFeature} className="w-4 h-4" />
                <span>Active</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="is_highlighted" checked={featureForm.is_highlighted} onChange={handleFeatureFormChange} disabled={savingFeature} className="w-4 h-4" />
                <span>Highlighted</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <FeatureDescriptionEditor value={featureForm.description_html} onChange={handleFeatureDescriptionChange} disabled={savingFeature} />
              <p className="text-xs text-gray-500 mt-2">Use this rich text editor to describe the feature. HTML is stored and rendered on the public view.</p>
            </div>

            <div className="flex justify-end items-center gap-3">
              {selectedFeatureId && (
                <Button
                  bsPrefix="custom-hamburger"
                  type="button"
                  onClick={handleNewFeature}
                  disabled={savingFeature}
                  className="border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2"
                >
                  Cancel
                </Button>
              )}
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                disabled={savingFeature}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2 flex items-center gap-2"
              >
                {savingFeature ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Save Feature
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeatureManager;
